import { Operations, ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult, checkAnswer } from "../Quiz/answer.ts";
import type { PickIndex } from "./chartGame.ts";

/*
 * The Product Game (ADR 0055) and its Sum Game variant (ADR 0098 §3), as pure
 * functions over a state value: two clips on 1-9, a 6 x 6 board, four in a row
 * wins. The component owns timing, focus and the DOM.
 *
 * The product board holds 36 distinct values, so a value names its square. The
 * sum board repeats the common sums, so squares are addressed by board index,
 * and a player whose sum sits on several free squares chooses which (the
 * "choosing" phase).
 */

export const FACTORS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export const SIDE = 6;
export const IN_A_ROW = 4;

export type ProductVariant = "product" | "sum";

/** The 36 distinct products of 1-9, ascending: the standard board. */
export const BOARD: number[] = Array.from(
    new Set(FACTORS.flatMap((a) => FACTORS.map((b) => a * b)))
).sort((x, y) => x - y);

/**
 * How many squares of the Sum Game board show each sum: symmetric about 10 and
 * totalling 36 (ADR 0098 §3).
 */
export const SUM_COUNTS: Record<number, number> = {
    2: 1, 3: 1, 4: 1, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4,
    11: 3, 12: 3, 13: 3, 14: 2, 15: 2, 16: 1, 17: 1, 18: 1,
};

/** The sums of the Sum Game board, ascending, before it is laid out at random. */
export const SUM_BOARD: number[] = Object.entries(SUM_COUNTS).flatMap(([sum, count]) =>
    Array.from({ length: count }, () => Number(sum))
);

export type Player = 0 | 1;
export type Clip = 0 | 1;

export interface Move {
    clip: Clip;
    value: number;
}

export interface ProductGameState {
    variant: ProductVariant;
    /** The value on each square, row by row. Fixed for the product variant, shuffled per Sum Game. */
    board: number[];
    clips: [number | null, number | null];
    /** Who holds each board square, by board index. */
    owners: (Player | null)[];
    turn: Player;
    phase: "playing" | "answering" | "choosing" | "over";
    /** The result (product or sum) waiting to be typed, after a move and before the claim. */
    pending: { a: number; b: number; product: number } | null;
    /** In the "choosing" phase, the free squares the player may tap; otherwise null. */
    candidates: number[] | null;
    winner: Player | "draw" | null;
    winLine: number[] | null;
    /** Board index of the latest claim, for highlighting. */
    lastClaim: number | null;
}

/** Every run of four squares in a line, as board indexes. */
export const LINES: number[][] = (() => {
    const lines: number[][] = [];
    const at = (r: number, c: number) => r * SIDE + c;
    const directions = [
        [0, 1],
        [1, 0],
        [1, 1],
        [1, -1],
    ];
    for (let r = 0; r < SIDE; r++) {
        for (let c = 0; c < SIDE; c++) {
            for (const [dr, dc] of directions) {
                const endR = r + dr * (IN_A_ROW - 1);
                const endC = c + dc * (IN_A_ROW - 1);
                if (endR < SIDE && endC >= 0 && endC < SIDE) {
                    lines.push(Array.from({ length: IN_A_ROW }, (_, i) => at(r + dr * i, c + dc * i)));
                }
            }
        }
    }
    return lines;
})();

/** The sum board in a random order, by Fisher-Yates over the injected `pick`. */
export function shuffledSumBoard(pick: PickIndex): number[] {
    const board = [...SUM_BOARD];
    for (let i = board.length - 1; i > 0; i--) {
        const j = pick(i + 1);
        [board[i], board[j]] = [board[j], board[i]];
    }
    return board;
}

/**
 * A fresh game. The product board is always the standard one; the Sum Game
 * board is laid out with `pick` (so a test can fix it), the identity layout if
 * none is given.
 */
export function newGame(variant: ProductVariant = "product", pick?: PickIndex): ProductGameState {
    const board = variant === "product" ? BOARD : pick ? shuffledSumBoard(pick) : [...SUM_BOARD];
    return {
        variant,
        board,
        clips: [null, null],
        owners: board.map(() => null),
        turn: 0,
        phase: "playing",
        pending: null,
        candidates: null,
        winner: null,
        winLine: null,
        lastClaim: null,
    };
}

/** The square of the standard product board that holds `product`. */
export function cellOf(product: number): number {
    return BOARD.indexOf(product);
}

/** Board indexes of the squares showing `value` that nobody holds yet. */
export function freeCellsOf(state: ProductGameState, value: number): number[] {
    const cells: number[] = [];
    state.board.forEach((v, i) => {
        if (v === value && state.owners[i] === null) {
            cells.push(i);
        }
    });
    return cells;
}

/** What two clips make in this variant. */
export function resultOf(variant: ProductVariant, a: number, b: number): number {
    return variant === "sum" ? a + b : a * b;
}

/** The operation a variant practises, for analytics. */
export function operationOfVariant(variant: ProductVariant): Operations {
    return variant === "sum" ? Operations.Addition : Operations.Multiplication;
}

/** The question the clips ask, in the shape the quiz marks. Built here: the chart's helper is multiplication-only. */
export function gameProblem(variant: ProductVariant, a: number, b: number): ProblemData {
    return {
        key: a * 100 + b,
        numbers: [a, b, resultOf(variant, a, b)],
        operator: variant === "sum" ? "+" : "x",
    };
}

function other(player: Player): Player {
    return player === 0 ? 1 : 0;
}

/**
 * Moves available now. The first move places clip A and claims nothing; the
 * second places clip B; after that one clip moves to a new factor. A move must
 * make a result with a free square (§1) - so the set does not depend on whose
 * turn it is, which is what makes "no legal move" a draw.
 */
export function legalMoves(state: ProductGameState): Move[] {
    if (state.phase !== "playing") {
        return [];
    }
    const [a, b] = state.clips;
    const free = (result: number) => freeCellsOf(state, result).length > 0;
    const make = (x: number, y: number) => resultOf(state.variant, x, y);
    if (a === null) {
        return FACTORS.map((value) => ({ clip: 0, value }));
    }
    if (b === null) {
        return FACTORS.filter((value) => free(make(a, value))).map((value) => ({ clip: 1, value }));
    }
    const moves: Move[] = [];
    for (const clip of [0, 1] as Clip[]) {
        const fixed = clip === 0 ? b : a;
        const current = clip === 0 ? a : b;
        for (const value of FACTORS) {
            if (value !== current && free(make(value, fixed))) {
                moves.push({ clip, value });
            }
        }
    }
    return moves;
}

export function isLegal(state: ProductGameState, move: Move): boolean {
    return legalMoves(state).some((m) => m.clip === move.clip && m.value === move.value);
}

/** Where a move would put the clips, and the result it would claim (none on the first). */
export function outcomeOf(state: ProductGameState, move: Move): { clips: [number | null, number | null]; product: number | null } {
    const clips: [number | null, number | null] = [...state.clips];
    clips[move.clip] = move.value;
    const product = clips[0] !== null && clips[1] !== null ? resultOf(state.variant, clips[0], clips[1]) : null;
    return { clips, product };
}

function passTurn(state: ProductGameState): ProductGameState {
    const next: ProductGameState = { ...state, turn: other(state.turn), phase: "playing", pending: null, candidates: null };
    if (legalMoves(next).length === 0) {
        return { ...next, phase: "over", winner: "draw" };
    }
    return next;
}

/** Makes a legal move. Placing clip A passes the turn; any other move waits for the answer. */
export function move(state: ProductGameState, m: Move): ProductGameState {
    if (!isLegal(state, m)) {
        return state;
    }
    const { clips, product } = outcomeOf(state, m);
    if (product === null) {
        return passTurn({ ...state, clips });
    }
    return { ...state, clips, phase: "answering", pending: { a: clips[0]!, b: clips[1]!, product } };
}

export function winningLine(owners: (Player | null)[], player: Player): number[] | null {
    return LINES.find((line) => line.every((i) => owners[i] === player)) ?? null;
}

/** Gives the current player the square at `index`, then ends the game or passes the turn. */
function claimSquare(state: ProductGameState, index: number): ProductGameState {
    const owners = [...state.owners];
    owners[index] = state.turn;
    const line = winningLine(owners, state.turn);
    const claimed: ProductGameState = { ...state, owners, lastClaim: index, candidates: null };
    if (line) {
        return { ...claimed, phase: "over", pending: null, winner: state.turn, winLine: line };
    }
    return passTurn(claimed);
}

/**
 * The answer for the move just made, as typed. Marked by the quiz's own
 * `checkAnswer`. Right claims the square - or, when the result sits on more
 * than one free square, waits for the player to choose which ("choosing");
 * wrong leaves the clip where it moved and passes the turn (§2). An empty box
 * is a wrong answer here - the move has already been made, so there is nothing
 * to skip back to.
 */
export function answer(state: ProductGameState, typed: string): { state: ProductGameState; result: AnswerResult } {
    if (state.phase !== "answering" || !state.pending) {
        return { state, result: "skipped" };
    }
    const checked = checkAnswer(gameProblem(state.variant, state.pending.a, state.pending.b), typed);
    const result: AnswerResult = checked === "correct" ? "correct" : "wrong";
    if (result === "wrong") {
        return { state: passTurn(state), result };
    }
    const free = freeCellsOf(state, state.pending.product);
    if (free.length === 0) {
        return { state: passTurn(state), result };
    }
    if (free.length > 1) {
        return { state: { ...state, phase: "choosing", candidates: free }, result };
    }
    return { state: claimSquare(state, free[0]), result };
}

/** The player's tap on one of the offered squares, in the "choosing" phase. */
export function choose(state: ProductGameState, index: number): ProductGameState {
    if (state.phase !== "choosing" || !state.candidates?.includes(index)) {
        return state;
    }
    return claimSquare(state, index);
}

/** The squares `player` would win the game by claiming next. */
function winningSquares(owners: (Player | null)[], player: Player): Set<number> {
    const squares = new Set<number>();
    for (const line of LINES) {
        const mine = line.filter((i) => owners[i] === player).length;
        const empty = line.filter((i) => owners[i] === null);
        if (mine === IN_A_ROW - 1 && empty.length === 1) {
            squares.add(empty[0]);
        }
    }
    return squares;
}

function lineScore(owners: (Player | null)[], index: number, player: Player): number {
    let score = 0;
    for (const line of LINES) {
        if (!line.includes(index) || line.some((i) => owners[i] === other(player))) {
            continue;
        }
        const mine = line.filter((i) => owners[i] === player).length;
        score += (mine + 1) * (mine + 1);
    }
    return score;
}

/** A move together with the square it would claim (null for the opening move). */
export interface Turn {
    move: Move;
    square: number | null;
}

/**
 * The computer's turn (§3), in order: win now; take the square that stops the
 * opponent's immediate win; avoid a move that hands them a winning reply; then
 * extend its own open lines most, ties broken by `pick`. It weighs every
 * (move, free square) pair, so on the Sum Game it also chooses which of
 * several squares showing the same sum to take; on the product board each move
 * has exactly one square and the choice is the multiplication AI's as ever.
 */
export function chooseTurn(state: ProductGameState, pick: PickIndex): Turn | null {
    const turns: Turn[] = [];
    for (const m of legalMoves(state)) {
        const { product } = outcomeOf(state, m);
        if (product === null) {
            turns.push({ move: m, square: null });
        } else {
            for (const square of freeCellsOf(state, product)) {
                turns.push({ move: m, square });
            }
        }
    }
    if (turns.length === 0) {
        return null;
    }
    const me = state.turn;
    const them = other(me);
    const pickOne = (list: Turn[]) => list[pick(list.length)];

    const myWins = winningSquares(state.owners, me);
    const winning = turns.filter((t) => myWins.has(t.square ?? -1));
    if (winning.length > 0) {
        return pickOne(winning);
    }

    const theirWins = winningSquares(state.owners, them);
    const blocking = turns.filter((t) => theirWins.has(t.square ?? -1));
    if (blocking.length > 0) {
        return pickOne(blocking);
    }

    const handsThemAWin = (t: Turn) => {
        const owners = [...state.owners];
        if (t.square !== null) {
            owners[t.square] = me;
        }
        const after: ProductGameState = { ...state, clips: outcomeOf(state, t.move).clips, owners, turn: them };
        const threats = winningSquares(owners, them);
        return legalMoves(after).some((reply) => {
            const { product } = outcomeOf(after, reply);
            return product !== null && freeCellsOf(after, product).some((i) => threats.has(i));
        });
    };
    const safe = turns.filter((t) => !handsThemAWin(t));
    const candidates = safe.length > 0 ? safe : turns;

    let best: Turn[] = [];
    let bestScore = -Infinity;
    for (const t of candidates) {
        const score = t.square === null ? 0 : lineScore(state.owners, t.square, me);
        if (score > bestScore) {
            bestScore = score;
            best = [t];
        } else if (score === bestScore) {
            best.push(t);
        }
    }
    return pickOne(best);
}

/** The computer's clip move alone, for callers that do not need the square. */
export function chooseMove(state: ProductGameState, pick: PickIndex): Move | null {
    return chooseTurn(state, pick)?.move ?? null;
}
